import { useCallback, useEffect, useState } from 'react';
import { api } from '../lib/api.js';

/**
 * useBranding — fetch the current tenant's branding settings.
 *
 * Endpoint: GET /api/dashboard/branding
 * Returns (post-Patch-4, no logo_r2_key): {
 *   name, logo_url, brand_color, brand_color_dark,
 *   public_tagline, public_phone, public_website, display_name_mode
 * }
 */
export function useBranding() {
  const [state, setState] = useState({
    branding: null,
    loading: true,
    error: null,
  });

  const load = useCallback(async () => {
    setState(s => ({ ...s, loading: true, error: null }));
    try {
      const data = await api.get('/api/dashboard/branding');
      setState({ branding: data.branding, loading: false, error: null });
    } catch (e) {
      setState({ branding: null, loading: false, error: e });
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return { ...state, refresh: load };
}

/**
 * Branding mutations.
 *
 * updateBranding — PUT /api/dashboard/branding with partial body
 * uploadLogo    — POST /api/dashboard/branding/logo (multipart)
 * deleteLogo    — DELETE /api/dashboard/branding/logo
 */
export function useBrandingMutations() {
  const updateBranding = useCallback(async (partial) => {
    const data = await api.put('/api/dashboard/branding', partial);
    return data.branding;
  }, []);

  const uploadLogo = useCallback(async (file) => {
    const form = new FormData();
    form.append('logo', file);
    const data = await api.post('/api/dashboard/branding/logo', form);
    return data; // { logo_url, logo_r2_key } — r2_key still sent from this endpoint
  }, []);

  const deleteLogo = useCallback(async () => {
    return api.del('/api/dashboard/branding/logo');
  }, []);

  return { updateBranding, uploadLogo, deleteLogo };
}
